import { ChangeDetectionStrategy, Component } from '@angular/core';
import {
  Dialog,
  LocalStorageService,
  SharedModule,
  getAppConfig,
  getServerLocation,
} from '@vendure/admin-ui/core';

@Component({
  selector: 'ft-marketing-preferences-download-dialog',
  template: `
    <ng-template vdrDialogTitle>Download marketing preferences</ng-template>
    <div class="flex items-center ">
      <button class="button primary" (click)="downloadCsv()">
        Download csv
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SharedModule],
})
export class MarketingPreferencesDownloadDialogComponent
  implements Dialog<boolean>
{
  constructor(private localStorageService: LocalStorageService) {}

  resolveWith: (result?: boolean) => void;

  close() {
    this.resolveWith(false);
  }

  /**
   * To download the CSV file, we make a fetch request to the `/marketing-preferences/export` endpoint
   * which is exposed by the controller in this plugin. This method of fetching the file blob
   * and then creating a download link was found to be a more reliable way to download the file
   * successfully than using an anchor tag with the `download` attribute, which was causing
   * issues in some browsers.
   */
  downloadCsv() {
    fetch(this.getDownloadLink(), {
      headers: this.getFetchHeaders(),
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.getDownloadFilename();
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  private getDownloadLink() {
    const serverLocation = getServerLocation();
    return `${serverLocation}/marketing-preferences/export`;
  }

  private getDownloadFilename() {
    // e.g. marketing-preferences-2021-09-01.csv
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `marketing-preferences-${year}-${month}-${day}.csv`;
  }

  /**
   * Because we are making a call to a REST-style endpoint, we do not go via the
   * usual DataService (which is what we would use to make GraphQL calls).
   * Therefore, in order to send the correct authentication headers, we need to
   * manually add them to the fetch request.
   */
  private getFetchHeaders(): Record<string, string> {
    const { tokenMethod, channelTokenKey } = getAppConfig();
    const headers: Record<string, string> = {};
    const channelToken = this.localStorageService.get('activeChannelToken');
    if (channelToken) {
      headers[channelTokenKey ?? 'vendure-token'] = channelToken;
    }
    if (tokenMethod === 'bearer') {
      const authToken = this.localStorageService.get('authToken');
      if (authToken) {
        headers.authorization = `Bearer ${authToken}`;
      }
    }
    return headers;
  }
}
